/**
 * IDs do chat: snowflakes de 64 bits no formato do Discord, com a mesma época
 * (2015-01-01T00:00:00Z). Os IDs importados do Discord e os gerados aqui ficam
 * na mesma ordem cronológica, então respostas, menções e links antigos seguem
 * válidos. No JSON vão como string (número passa de 2^53).
 *
 * Layout: timestamp (42 bits, ms desde a época) | worker (5) | processo (5) | sequência (12).
 */
export type Snowflake = string;

export const SNOWFLAKE_EPOCH_MS = 1420070400000;

const EPOCH = BigInt(SNOWFLAKE_EPOCH_MS);
const MAX_SNOWFLAKE = (1n << 64n) - 1n;

export function isSnowflake(value: unknown): value is Snowflake {
  if (typeof value !== 'string' || !/^\d{1,20}$/.test(value)) return false;
  return BigInt(value) <= MAX_SNOWFLAKE;
}

export function snowflakeTimestamp(id: Snowflake): number {
  return Number((BigInt(id) >> 22n) + EPOCH);
}

export function snowflakeDate(id: Snowflake): Date {
  return new Date(snowflakeTimestamp(id));
}

/**
 * Menor snowflake possível num instante: serve de limite para "mensagens
 * depois de tal data" (`after=snowflakeAt(data)`).
 */
export function snowflakeAt(
  time: Date | number,
  worker = 0,
  process = 0,
  sequence = 0,
): Snowflake {
  const ms = BigInt(typeof time === 'number' ? time : time.getTime());
  if (ms < EPOCH) {
    throw new RangeError('Snowflake antes de 2015-01-01 não existe.');
  }
  return (
    ((ms - EPOCH) << 22n) |
    (BigInt(worker & 0x1f) << 17n) |
    (BigInt(process & 0x1f) << 12n) |
    BigInt(sequence & 0xfff)
  ).toString();
}

export function compareSnowflakes(a: Snowflake, b: Snowflake): number {
  const x = BigInt(a);
  const y = BigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
}
